import { useState } from "react";
import { Calendar, Flag } from "lucide-react";
import { ROLE_COLORS } from "@/lib/palettes";

const PRIORITIES = ["low", "medium", "high"];
const PRIORITY_STYLES = {
  low: "bg-sky-500/20 text-sky-700 dark:text-sky-300",
  medium: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
  high: "bg-red-500/20 text-red-700 dark:text-red-300",
};
const STATUSES = ["todo", "doing", "done"];

export default function TaskCard({ card, users, me, onData }) {
  const d = card.data || {};
  const [title, setTitle] = useState(d.title || "");
  const [desc, setDesc] = useState(d.description || "");

  const canAssign = (u) => {
    if (u.id === me.id) return true;
    if (me.role === "admin") return true;
    if (me.role === "manager" && u.role === "executive") return true;
    return false;
  };
  const assignables = users.filter(canAssign);
  const isMine = card.owner_id === me.id;

  return (
    <div className="space-y-2">
      <input
        data-testid="task-title-input"
        value={title} onChange={(e) => setTitle(e.target.value)}
        onBlur={() => title !== d.title && onData({ title })}
        placeholder="What needs doing?"
        className="w-full bg-transparent font-display text-base font-bold outline-none placeholder:opacity-40"
      />
      <textarea
        data-testid="task-desc-input"
        value={desc} onChange={(e) => setDesc(e.target.value)}
        onBlur={() => desc !== d.description && onData({ description: desc })}
        placeholder="Details..." rows={2}
        className="w-full resize-none bg-transparent text-sm outline-none placeholder:opacity-40"
      />
      <div className="flex flex-wrap items-center gap-1.5">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`task-status-${s}`}
            onClick={() => onData({ status: s })}
            className={`rounded-full px-2.5 py-0.5 text-xs font-mono uppercase tracking-wide transition-all ${
              (d.status || "todo") === s ? "bg-black/15 dark:bg-white/20 font-bold scale-105" : "opacity-50 hover:opacity-80"
            }`}>
            {s}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${PRIORITY_STYLES[d.priority || "medium"]}`}>
          <Flag className="h-3 w-3" />
          <select data-testid="task-priority-select" value={d.priority || "medium"}
            onChange={(e) => onData({ priority: e.target.value })}
            className="bg-transparent outline-none cursor-pointer">
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </span>
        <span className="flex items-center gap-1 rounded-full bg-black/10 dark:bg-white/10 px-2 py-0.5">
          <Calendar className="h-3 w-3" />
          <input data-testid="task-due-input" type="date" value={d.due_date || ""}
            onChange={(e) => onData({ due_date: e.target.value })}
            className="bg-transparent outline-none cursor-pointer w-[105px]" />
        </span>
      </div>
      {isMine && (
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono uppercase opacity-60">Assign:</span>
          <select data-testid="task-assignee-select" value={d.assignee_id || me.id}
            onChange={(e) => onData({ assignee_id: e.target.value })}
            className="flex-1 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1 outline-none cursor-pointer">
            {assignables.map((u) => (
              <option key={u.id} value={u.id}>{u.id === me.id ? "Me" : `${u.name} (${u.role})`}</option>
            ))}
          </select>
        </div>
      )}
      {d.assignee_id && d.assignee_id !== card.owner_id && d.assignee_name && (
        <div className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase ${ROLE_COLORS.executive}`}
          data-testid="task-assignee-badge">
          → {d.assignee_name}
        </div>
      )}
    </div>
  );
}
